import { Ban, Building2, CircleHelp, Globe, type LucideIcon } from "lucide-react";
import { useCallback } from "react";

import { ACCESS_CATALOG } from "@/modules/dashboard/settings/constants/accessCatalog.constants";
import type { AccessRole } from "@/modules/dashboard/settings/types/access.types";
import { resolveRoleGroup } from "@/modules/dashboard/settings/utils/roleModuleGroup";
import { useTranslations } from "@/shared/lang/useTranslations";

export interface RoleReach {
  label: string;
  icon: LucideIcon;
}

/**
 * What a role actually reaches, for the table's "Alcance" column.
 *
 * The answer comes from `resolveRoleGroup`, which derives it from the role's STATEMENTS — what it grants —
 * rather than from its name or slug, which are labels a role can carry regardless of its permissions. The
 * four non-module buckets are not fallbacks for "we could not tell": each says something specific, and
 * `empty` in particular is a real finding (assigning that role grants nothing).
 *
 * The labels are the ones the assign-roles drawer already uses for the same buckets
 * (`AssignRolesDrawer.tsx:234-247`), so the same role is described identically wherever it appears. The two
 * mappings are twins; if a third consumer appears, hoist this next to `resolveRoleGroup`.
 */
export function useRoleReach() {
  const { t } = useTranslations("settings");

  return useCallback(
    (role: AccessRole): RoleReach => {
      const group = resolveRoleGroup(role);

      if (group.kind === "module") {
        const mod = ACCESS_CATALOG.find((m) => m.id === group.moduleId);
        // An id outside the mirror still renders, under its raw resource: hiding it would hide access.
        return mod ? { label: t(mod.labelKey), icon: mod.icon } : { label: group.moduleId, icon: CircleHelp };
      }
      if (group.kind === "broad") return { label: t("access_drawer_group_broad"), icon: Globe };
      if (group.kind === "org") return { label: t("access_drawer_group_org"), icon: Building2 };
      if (group.kind === "empty") return { label: t("access_drawer_group_empty"), icon: Ban };
      return { label: t("access_drawer_group_unknown"), icon: CircleHelp };
    },
    [t],
  );
}
